import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createWidget, type BitPalmAgentInstance } from '@bitpalm/ai-agents';
import useCookieConsentStore from '../../stores/cookieConsentStore';
import { isPublicPath } from '../../utils/publicPaths';

const SLUG = process.env.REACT_APP_BITPALM_AGENT_SLUG;

const isHeadlessBrowser = (): boolean => {
    return typeof navigator !== 'undefined' && navigator.webdriver === true;
};

/**
 * Without chat consent the widget runs in its own opt-out mode: it still
 * answers, but keeps no visitor ID, stitches no form data and sends no
 * tracking beacons. It reads the flag when it starts, so this runs first.
 */
const applyChatConsent = (allowed: boolean) => {
    try {
        if (allowed) {
            localStorage.removeItem('_bp_optout');
            return;
        }
        localStorage.setItem('_bp_optout', '1');
        localStorage.removeItem('_bp_vid');
        localStorage.removeItem('_bp_visited');
        document.cookie = '_bp_vid=; path=/; max-age=0; SameSite=Lax';
    } catch {
        // Without storage the widget cannot keep a visitor ID either.
    }
};

const BitPalmAgent = () => {
    const location = useLocation();
    const { i18n } = useTranslation();
    const locale = i18n.language;
    const chatAllowed = useCookieConsentStore((state) => state.choice?.chat === true);

    useEffect(() => {
        if (!SLUG) return;
        if (isHeadlessBrowser()) return;
        if (!isPublicPath(location.pathname)) return;

        applyChatConsent(chatAllowed);
        const widget: BitPalmAgentInstance = createWidget({
            slug: SLUG,
            locale,
            zIndex: 9999,
            autoIdentify: false,
        });

        return () => {
            widget.destroy();
        };
    }, [location.pathname, locale, chatAllowed]);

    return null;
};

export default BitPalmAgent;
